<template>
  <div :class="[containerClass, wrapperClass]">
    <div
      :class="[
        contentPaddingClass,
        expandable && hasContent && useCardPadding
          ? 'border-bottom border-translucent'
          : '',
      ]"
    >
      <div
        :class="[
          'd-flex flex-column flex-md-row align-items-center justify-content-between border-0',
          titlePaddingClass,
        ]"
      >
        <slot name="title"></slot>
      </div>
      <template v-if="hasContent">
        <hr v-if="!showCollapse && !useCardPadding" class="m-0 my-1" />
        <div v-show="showCollapse">
          <div
            :class="
              framedContent
                ? 'border rounded-3 my-2 p-0 box-shadow overflow-hidden'
                : 'my-2'
            "
          >
            <slot name="content"></slot>
          </div>
        </div>
      </template>
    </div>
    <button
      v-if="expandable && hasContent"
      class="btn btn-text w-100"
      :class="[buttonAlignmentClass, buttonPaddingClass]"
      @click="toggleCollapse()"
      type="button"
      :aria-expanded="showCollapse"
      :aria-label="buttonText"
    >
      <font-awesome-icon
        :icon="showCollapse ? 'chevron-up' : 'chevron-down'"
        class="pe-1"
      />
      <span class="p-0 d-inline">
        {{ buttonText }}
      </span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref, useSlots } from "vue";

const props = withDefaults(
  defineProps<{
    buttonText?: string;
    expandable?: boolean;
    containerClass?: string;
    collapsed?: boolean;
    buttonAlignment?: "left" | "center";
    useCardPadding?: boolean;
    /** Frames the expanded content in a bordered box; off where the content brings its own frames. */
    framedContent?: boolean;
  }>(),
  {
    buttonText: "",
    expandable: true,
    containerClass: "bg-body",
    collapsed: false,
    buttonAlignment: "center",
    useCardPadding: false,
    framedContent: true,
  }
);

const emit = defineEmits<{
  setForceToggle: [forceToggleCollapse: (value: boolean) => void];
  /** The content was shown or hidden, by the toggle or by `forceToggleCollapse`. */
  toggled: [expanded: boolean];
}>();

const slots = useSlots();
const hasContent = !!slots.content;

const showCollapse = ref(!props.collapsed);

const toggleCollapse = () => {
  showCollapse.value = !showCollapse.value;
  emit("toggled", showCollapse.value);
};

const forceToggleCollapse = (value: boolean) => {
  showCollapse.value = value;
  emit("toggled", value);
};

const contentPaddingClass = props.useCardPadding ? "card-body" : "p-2 pb-0";
const titlePaddingClass = props.useCardPadding ? "" : "p-1";

// Only add border and rounded if not already a card
const isCard = props.containerClass && props.containerClass.includes("card");
const wrapperClass = !isCard ? "border rounded-2" : "";

const buttonAlignmentClass =
  props.buttonAlignment === "left" ? "text-start" : "text-center";

const buttonPaddingClass =
  props.useCardPadding || props.buttonAlignment === "left"
    ? "px-4 py-3"
    : "p-1 pb-2";

// Expose the method
emit("setForceToggle", forceToggleCollapse);
</script>
